import React, { useContext, useEffect, useState } from 'react'
import {
  ShieldCheck,
  Archive,
  Trash2,
  RefreshCw,
  Search,
  Filter,
  Calendar,
  Layers,
  Clock,
  User,
  Database,
  ArrowRight,
  HardDrive,
  Eye,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Play,
  RotateCcw,
  Sparkles,
} from 'lucide-react'
import UserContext, { Roles } from '../../../contexts/UserContext'
import AuditApiService, {
  AuditLogEntry,
  AuditRetentionStats,
  AuditArchiveSummary,
  AuditRetentionTaskItem,
} from '../../../services/AuditApiService'
import UnarchiveModal from './UnarchiveModal'
import UserAvatar from '../../../components/UserAvatar'

const AuditDashboard: React.FC = () => {
  const { user } = useContext(UserContext)
  const isGlobalAdmin =
    (user?.role as any) === Roles.globalAdmin ||
    (user?.role as any) === 'ADMIN_GLOBAL' ||
    (typeof user?.role === 'string' && user.role.toUpperCase() === 'ADMIN_GLOBAL')

  const [activeTab, setActiveTab] = useState<'live' | 'archive' | 'tasks'>('live')
  const [stats, setStats] = useState<AuditRetentionStats | null>(null)
  const [loadingStats, setLoadingStats] = useState(false)

  const [liveLogs, setLiveLogs] = useState<AuditLogEntry[]>([])
  const [liveTotal, setLiveTotal] = useState(0)
  const [livePage, setLivePage] = useState(1)
  const [liveLimit] = useState(20)
  const [loadingLive, setLoadingLive] = useState(false)

  const [search, setSearch] = useState('')
  const [selectedTable, setSelectedTable] = useState('')
  const [selectedAction, setSelectedAction] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  const [archiveSummaries, setArchiveSummaries] = useState<AuditArchiveSummary[]>([])
  const [loadingArchive, setLoadingArchive] = useState(false)

  const [tasks, setTasks] = useState<AuditRetentionTaskItem[]>([])
  const [tasksTotal, setTasksTotal] = useState(0)
  const [tasksPage, setTasksPage] = useState(1)
  const [loadingTasks, setLoadingTasks] = useState(false)

  const [selectedLogForDiff, setSelectedLogForDiff] = useState<AuditLogEntry | null>(null)
  const [isUnarchiveModalOpen, setIsUnarchiveModalOpen] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  useEffect(() => {
    if (!isGlobalAdmin) return
    loadStats()
  }, [isGlobalAdmin])

  useEffect(() => {
    if (!isGlobalAdmin) return
    if (activeTab === 'live') {
      loadLiveLogs()
    } else if (activeTab === 'archive') {
      loadArchiveSummaries()
    } else if (activeTab === 'tasks') {
      loadTasks()
    }
  }, [activeTab, livePage, selectedTable, selectedAction, startDate, endDate, tasksPage, isGlobalAdmin])

  const loadStats = async () => {
    setLoadingStats(true)
    try {
      const data = await AuditApiService.getAdminStats()
      setStats(data)
    } catch (error) {
      console.error('Erro ao carregar estatísticas de retenção:', error)
    } finally {
      setLoadingStats(false)
    }
  }

  const loadLiveLogs = async () => {
    setLoadingLive(true)
    try {
      const result = await AuditApiService.getAdminLiveLogs({
        page: livePage,
        limit: liveLimit,
        search: search.trim() || undefined,
        tableName: selectedTable || undefined,
        action: selectedAction || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      })
      setLiveLogs(result.records || [])
      setLiveTotal(result.total || 0)
    } catch (error) {
      console.error('Erro ao carregar logs em tempo real:', error)
      setLiveLogs([])
      setLiveTotal(0)
    } finally {
      setLoadingLive(false)
    }
  }

  const loadArchiveSummaries = async () => {
    setLoadingArchive(true)
    try {
      const data = await AuditApiService.getAdminArchives()
      setArchiveSummaries(data || [])
    } catch (error) {
      console.error('Erro ao carregar sumários de arquivo:', error)
      setArchiveSummaries([])
    } finally {
      setLoadingArchive(false)
    }
  }

  const loadTasks = async () => {
    setLoadingTasks(true)
    try {
      const result = await AuditApiService.getRetentionTasks(undefined, tasksPage, 20)
      setTasks(result.tasks || [])
      setTasksTotal(result.total || 0)
    } catch (error) {
      console.error('Erro ao carregar tarefas de retenção:', error)
      setTasks([])
      setTasksTotal(0)
    } finally {
      setLoadingTasks(false)
    }
  }

  const handleRunArchive = async () => {
    if (!window.confirm('Deseja mover os logs com mais de 30 dias para o arquivo histórico agora?')) return
    setActionLoading(true)
    setActionMessage(null)
    try {
      const res = await AuditApiService.runArchiveTask(30)
      setActionMessage({ type: 'success', text: res.message || 'Tarefa de arquivamento executada com sucesso.' })
      loadStats()
      if (activeTab === 'live') loadLiveLogs()
      if (activeTab === 'archive') loadArchiveSummaries()
      if (activeTab === 'tasks') loadTasks()
    } catch (err: any) {
      setActionMessage({ type: 'error', text: err?.response?.data?.message || 'Erro ao executar arquivamento.' })
    } finally {
      setActionLoading(false)
    }
  }

  const handleRunPurge = async () => {
    if (!window.confirm('ATENÇÃO: Deseja purgar definitivamente os registros arquivados com mais de 6 meses (180 dias)?')) return
    setActionLoading(true)
    setActionMessage(null)
    try {
      const res = await AuditApiService.runPurgeTask(6)
      setActionMessage({ type: 'success', text: res.message || 'Purga de 6 meses concluída com sucesso.' })
      loadStats()
      if (activeTab === 'archive') loadArchiveSummaries()
      if (activeTab === 'tasks') loadTasks()
    } catch (err: any) {
      setActionMessage({ type: 'error', text: err?.response?.data?.message || 'Erro ao purgar registros.' })
    } finally {
      setActionLoading(false)
    }
  }

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setLivePage(1)
    loadLiveLogs()
  }

  const handleClearFilters = () => {
    setSearch('')
    setSelectedTable('')
    setSelectedAction('')
    setStartDate('')
    setEndDate('')
    setLivePage(1)
    setTimeout(() => {
      loadLiveLogs()
    }, 50)
  }

  if (!isGlobalAdmin) {
    return (
      <div style={{ padding: '60px 20px', textAlign: 'center', backgroundColor: '#F8FAFC', minHeight: '80vh' }}>
        <div
          style={{
            maxWidth: '480px',
            margin: '0 auto',
            backgroundColor: '#fff',
            padding: '40px',
            borderRadius: '16px',
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
          }}
        >
          <div
            style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: '#FEE2E2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#DC2626',
              margin: '0 auto 20px',
            }}
          >
            <ShieldCheck size={32} />
          </div>
          <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#111827', marginBottom: '8px' }}>
            Acesso Restrito a Administradores Globais
          </h2>
          <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: 1.5 }}>
            O módulo de auditoria global e governança temporal de dados está disponível exclusivamente para usuários com nível de acesso <strong>admin_global</strong>.
          </p>
        </div>
      </div>
    )
  }

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return '-'
    const d = new Date(dateStr)
    return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  }

  const renderActionBadge = (action: string) => {
    const act = action.toUpperCase()
    if (act === 'INSERT' || act === 'CREATE') {
      return (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '3px 8px',
            borderRadius: '6px',
            backgroundColor: '#DCFCE7',
            color: '#166534',
            fontSize: '12px',
            fontWeight: 600,
          }}
        >
          Criado
        </span>
      )
    }
    if (act === 'UPDATE') {
      return (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '3px 8px',
            borderRadius: '6px',
            backgroundColor: '#EFF6FF',
            color: '#1E40AF',
            fontSize: '12px',
            fontWeight: 600,
          }}
        >
          Atualizado
        </span>
      )
    }
    if (act === 'DELETE') {
      return (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '3px 8px',
            borderRadius: '6px',
            backgroundColor: '#FEE2E2',
            color: '#991B1B',
            fontSize: '12px',
            fontWeight: 600,
          }}
        >
          Excluído
        </span>
      )
    }
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          padding: '3px 8px',
          borderRadius: '6px',
          backgroundColor: '#F3F4F6',
          color: '#374151',
          fontSize: '12px',
          fontWeight: 600,
        }}
      >
        {action}
      </span>
    )
  }

  return (
    <div style={{ padding: '24px 32px', backgroundColor: '#F8FAFC', minHeight: '100vh', fontFamily: 'inherit' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                backgroundColor: '#0090D3',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
              }}
            >
              <ShieldCheck size={24} />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 700, color: '#0F172A' }}>
                Auditoria & Governança Temporal
              </h1>
              <p style={{ margin: 0, fontSize: '13px', color: '#64748B' }}>
                Gerenciamento em 2 camadas: 30 dias quentes em tempo real e 6 meses compactados em arquivo histórico
              </p>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            onClick={() => setIsUnarchiveModalOpen(true)}
            style={{
              padding: '10px 16px',
              borderRadius: '8px',
              border: '1px solid #0090D3',
              backgroundColor: '#fff',
              color: '#0090D3',
              fontWeight: 600,
              fontSize: '13px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s',
            }}
          >
            <RotateCcw size={16} />
            <span>Desarquivar Período</span>
          </button>

          <button
            onClick={handleRunArchive}
            disabled={actionLoading}
            style={{
              padding: '10px 16px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: '#0090D3',
              color: '#fff',
              fontWeight: 600,
              fontSize: '13px',
              cursor: actionLoading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              opacity: actionLoading ? 0.7 : 1,
              boxShadow: '0 2px 4px rgba(0, 144, 211, 0.2)',
            }}
          >
            {actionLoading ? <RefreshCw size={16} className="spin" /> : <Archive size={16} />}
            <span>Arquivar Logs (&gt;30d)</span>
          </button>
        </div>
      </div>

      {actionMessage && (
        <div
          style={{
            padding: '14px 18px',
            backgroundColor: actionMessage.type === 'success' ? '#DCFCE7' : '#FEE2E2',
            color: actionMessage.type === 'success' ? '#166534' : '#991B1B',
            borderRadius: '10px',
            fontSize: '13px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {actionMessage.type === 'success' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
            <span>{actionMessage.text}</span>
          </div>
          <button
            onClick={() => setActionMessage(null)}
            style={{ border: 'none', background: 'none', color: 'inherit', cursor: 'pointer' }}
          >
            ✕
          </button>
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: '16px',
          marginBottom: '24px',
        }}
      >
        <div
          style={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            border: '1px solid #E2E8F0',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: 600, color: '#64748B' }}>Auditoria Ativa (30 Dias)</span>
            <div
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                backgroundColor: '#EFF6FF',
                color: '#2563EB',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Layers size={18} />
            </div>
          </div>
          <div style={{ fontSize: '28px', fontWeight: 700, color: '#0F172A', marginBottom: '4px' }}>
            {stats?.activeLogsCount?.toLocaleString('pt-BR') ?? 0}
          </div>
          <div style={{ fontSize: '12px', color: '#10B981', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Sparkles size={14} />
            <span>Consultas em tempo real ativas</span>
          </div>
        </div>

        <div
          style={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            border: '1px solid #E2E8F0',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: 600, color: '#64748B' }}>Arquivo Histórico (6 Meses)</span>
            <div
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                backgroundColor: '#F0FDF4',
                color: '#16A34A',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Archive size={18} />
            </div>
          </div>
          <div style={{ fontSize: '28px', fontWeight: 700, color: '#0F172A', marginBottom: '4px' }}>
            {stats?.archivedLogsCount?.toLocaleString('pt-BR') ?? 0}
          </div>
          <div style={{ fontSize: '12px', color: '#64748B' }}>
            Período compactado de 31 a 180 dias
          </div>
        </div>

        <div
          style={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            border: '1px solid #E2E8F0',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: 600, color: '#64748B' }}>Registros Excluídos (Lixeira)</span>
            <div
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                backgroundColor: '#FEF2F2',
                color: '#DC2626',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Trash2 size={18} />
            </div>
          </div>
          <div style={{ fontSize: '28px', fontWeight: 700, color: '#0F172A', marginBottom: '4px' }}>
            {stats?.deletedLogsCount?.toLocaleString('pt-BR') ?? 0}
          </div>
          <div style={{ fontSize: '12px', color: '#64748B' }}>
            Passíveis de restauração imediata
          </div>
        </div>

        <div
          style={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            border: '1px solid #E2E8F0',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: 600, color: '#64748B' }}>Espaço Economizado</span>
            <div
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                backgroundColor: '#FAF5FF',
                color: '#9333EA',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <HardDrive size={18} />
            </div>
          </div>
          <div style={{ fontSize: '28px', fontWeight: 700, color: '#0F172A', marginBottom: '4px' }}>
            {stats?.estimatedSpaceSavedKb ? `${(stats.estimatedSpaceSavedKb / 1024).toFixed(1)} MB` : '0 MB'}
          </div>
          <div style={{ fontSize: '12px', color: '#9333EA' }}>
            Otimização contínua de disco
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          borderBottom: '1px solid #E2E8F0',
          marginBottom: '20px',
        }}
      >
        <button
          onClick={() => setActiveTab('live')}
          style={{
            padding: '12px 20px',
            border: 'none',
            borderBottom: activeTab === 'live' ? '2px solid #0090D3' : '2px solid transparent',
            backgroundColor: 'transparent',
            color: activeTab === 'live' ? '#0090D3' : '#64748B',
            fontWeight: activeTab === 'live' ? 700 : 500,
            fontSize: '14px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Layers size={18} />
          <span>Auditoria em Tempo Real (30 Dias)</span>
        </button>

        <button
          onClick={() => setActiveTab('archive')}
          style={{
            padding: '12px 20px',
            border: 'none',
            borderBottom: activeTab === 'archive' ? '2px solid #0090D3' : '2px solid transparent',
            backgroundColor: 'transparent',
            color: activeTab === 'archive' ? '#0090D3' : '#64748B',
            fontWeight: activeTab === 'archive' ? 700 : 500,
            fontSize: '14px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Archive size={18} />
          <span>Arquivo Histórico (6 Meses)</span>
        </button>

        <button
          onClick={() => setActiveTab('tasks')}
          style={{
            padding: '12px 20px',
            border: 'none',
            borderBottom: activeTab === 'tasks' ? '2px solid #0090D3' : '2px solid transparent',
            backgroundColor: 'transparent',
            color: activeTab === 'tasks' ? '#0090D3' : '#64748B',
            fontWeight: activeTab === 'tasks' ? 700 : 500,
            fontSize: '14px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Clock size={18} />
          <span>Tarefas & Retenção</span>
        </button>
      </div>

      {activeTab === 'live' && (
        <div
          style={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            border: '1px solid #E2E8F0',
            overflow: 'hidden',
          }}
        >
          <form
            onSubmit={handleSearchSubmit}
            style={{
              padding: '16px 20px',
              borderBottom: '1px solid #E2E8F0',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: '12px',
              backgroundColor: '#FAFAFA',
            }}
          >
            <div style={{ flex: '1 1 200px', position: 'relative' }}>
              <Search
                size={16}
                color="#94A3B8"
                style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }}
              />
              <input
                type="text"
                placeholder="Buscar por ID ou Tabela..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px 8px 36px',
                  border: '1px solid #CBD5E1',
                  borderRadius: '8px',
                  fontSize: '13px',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <select
              value={selectedAction}
              onChange={(e) => setSelectedAction(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #CBD5E1',
                borderRadius: '8px',
                fontSize: '13px',
                backgroundColor: '#fff',
                color: '#334155',
              }}
            >
              <option value="">Todas as Ações</option>
              <option value="INSERT">Criação (INSERT)</option>
              <option value="UPDATE">Atualização (UPDATE)</option>
              <option value="DELETE">Exclusão (DELETE)</option>
            </select>

            <input
              type="text"
              placeholder="Filtrar por Tabela..."
              value={selectedTable}
              onChange={(e) => setSelectedTable(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #CBD5E1',
                borderRadius: '8px',
                fontSize: '13px',
                width: '160px',
              }}
            />

            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{
                  padding: '7px 10px',
                  border: '1px solid #CBD5E1',
                  borderRadius: '8px',
                  fontSize: '13px',
                }}
              />
              <span style={{ color: '#94A3B8' }}>até</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{
                  padding: '7px 10px',
                  border: '1px solid #CBD5E1',
                  borderRadius: '8px',
                  fontSize: '13px',
                }}
              />
            </div>

            <button
              type="submit"
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                backgroundColor: '#0090D3',
                color: '#fff',
                border: 'none',
                fontWeight: 600,
                fontSize: '13px',
                cursor: 'pointer',
              }}
            >
              Filtrar
            </button>

            {(search || selectedTable || selectedAction || startDate || endDate) && (
              <button
                type="button"
                onClick={handleClearFilters}
                style={{
                  padding: '8px 14px',
                  borderRadius: '8px',
                  backgroundColor: '#E2E8F0',
                  color: '#475569',
                  border: 'none',
                  fontWeight: 500,
                  fontSize: '13px',
                  cursor: 'pointer',
                }}
              >
                Limpar
              </button>
            )}
          </form>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
              <thead>
                <tr style={{ backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0', color: '#475569', fontWeight: 600 }}>
                  <th style={{ padding: '12px 16px' }}>Data / Hora</th>
                  <th style={{ padding: '12px 16px' }}>Ação</th>
                  <th style={{ padding: '12px 16px' }}>Tabela / Módulo</th>
                  <th style={{ padding: '12px 16px' }}>ID Registro</th>
                  <th style={{ padding: '12px 16px' }}>Autor da Ação</th>
                  <th style={{ padding: '12px 16px' }}>Tenant ID</th>
                  <th style={{ padding: '12px 16px', textAlign: 'right' }}>Detalhes</th>
                </tr>
              </thead>
              <tbody>
                {loadingLive ? (
                  <tr>
                    <td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: '#64748B' }}>
                      <RefreshCw size={24} className="spin" style={{ margin: '0 auto 8px' }} />
                      <div>Carregando logs de auditoria...</div>
                    </td>
                  </tr>
                ) : liveLogs.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: '#64748B' }}>
                      Nenhum registro de auditoria encontrado para os filtros selecionados.
                    </td>
                  </tr>
                ) : (
                  liveLogs.map((log) => (
                    <tr
                      key={log.id}
                      style={{
                        borderBottom: '1px solid #F1F5F9',
                        transition: 'background-color 0.15s',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#F8FAFC')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#fff')}
                    >
                      <td style={{ padding: '12px 16px', color: '#334155', whiteSpace: 'nowrap' }}>
                        {formatDate(log.changedAt)}
                      </td>
                      <td style={{ padding: '12px 16px' }}>{renderActionBadge(log.action)}</td>
                      <td style={{ padding: '12px 16px', fontWeight: 600, color: '#0F172A' }}>{log.tableName}</td>
                      <td style={{ padding: '12px 16px', color: '#64748B', fontFamily: 'monospace' }}>#{log.recordId}</td>
                      <td style={{ padding: '12px 16px' }}>
                        {log.ChangedBy ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <UserAvatar
                              src={log.ChangedBy.picture}
                              name={log.ChangedBy.name}
                              size={24}
                            />
                            <div>
                              <div style={{ fontWeight: 500, color: '#1E293B' }}>{log.ChangedBy.name}</div>
                              <div style={{ fontSize: '11px', color: '#94A3B8' }}>{log.ChangedBy.email}</div>
                            </div>
                          </div>
                        ) : (
                          <span style={{ color: '#94A3B8' }}>Sistema / Automático</span>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px', color: '#64748B' }}>
                        {log.Tenant?.name ? `${log.Tenant.name} (${log.tenantId})` : `Tenant #${log.tenantId}`}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                        <button
                          onClick={() => setSelectedLogForDiff(log)}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '6px',
                            border: '1px solid #E2E8F0',
                            backgroundColor: '#fff',
                            color: '#0090D3',
                            fontSize: '12px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                          }}
                        >
                          <Eye size={14} />
                          <span>Ver Diff</span>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div
            style={{
              padding: '12px 20px',
              borderTop: '1px solid #E2E8F0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: '#FAFAFA',
              fontSize: '13px',
              color: '#64748B',
            }}
          >
            <div>
              Mostrando {liveLogs.length} de {liveTotal} registros
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                onClick={() => setLivePage((p) => Math.max(1, p - 1))}
                disabled={livePage === 1 || loadingLive}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: '1px solid #CBD5E1',
                  backgroundColor: '#fff',
                  cursor: livePage === 1 ? 'not-allowed' : 'pointer',
                  opacity: livePage === 1 ? 0.5 : 1,
                }}
              >
                Anterior
              </button>
              <span>Página {livePage}</span>
              <button
                onClick={() => setLivePage((p) => p + 1)}
                disabled={liveLogs.length < liveLimit || loadingLive}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: '1px solid #CBD5E1',
                  backgroundColor: '#fff',
                  cursor: liveLogs.length < liveLimit ? 'not-allowed' : 'pointer',
                  opacity: liveLogs.length < liveLimit ? 0.5 : 1,
                }}
              >
                Próxima
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'archive' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              border: '1px solid #E2E8F0',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#0F172A' }}>
                  Períodos Compactados em Arquivo Histórico
                </h3>
                <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748B' }}>
                  Sumário consolidado mês a mês das alterações arquivadas (preservadas por até 6 meses)
                </p>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => setIsUnarchiveModalOpen(true)}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '8px',
                    backgroundColor: '#0090D3',
                    color: '#fff',
                    border: 'none',
                    fontWeight: 600,
                    fontSize: '13px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <RotateCcw size={16} />
                  <span>Desarquivar Período Específico</span>
                </button>

                <button
                  onClick={handleRunPurge}
                  disabled={actionLoading}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '8px',
                    backgroundColor: '#FEF2F2',
                    color: '#DC2626',
                    border: '1px solid #FCA5A5',
                    fontWeight: 600,
                    fontSize: '13px',
                    cursor: actionLoading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <Trash2 size={16} />
                  <span>Purgar Registros &gt; 6 Meses</span>
                </button>
              </div>
            </div>

            {loadingArchive ? (
              <div style={{ padding: '30px', textAlign: 'center', color: '#64748B' }}>
                <RefreshCw size={24} className="spin" style={{ margin: '0 auto 8px' }} />
                <div>Carregando arquivos históricos...</div>
              </div>
            ) : archiveSummaries.length === 0 ? (
              <div style={{ padding: '30px', textAlign: 'center', color: '#64748B' }}>
                Nenhum período arquivado no momento. O arquivo é alimentado quando os logs atingem 30 dias.
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
                {archiveSummaries.map((summary) => (
                  <div
                    key={summary.monthYear}
                    style={{
                      border: '1px solid #E2E8F0',
                      borderRadius: '10px',
                      padding: '16px',
                      backgroundColor: '#F8FAFC',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontSize: '15px', fontWeight: 700, color: '#0F172A' }}>
                        {summary.monthYear}
                      </span>
                      <span
                        style={{
                          padding: '2px 8px',
                          borderRadius: '6px',
                          backgroundColor: '#E0F2FE',
                          color: '#0369A1',
                          fontSize: '11px',
                          fontWeight: 600,
                        }}
                      >
                        {summary.tablesCount} tabelas
                      </span>
                    </div>

                    <div style={{ fontSize: '20px', fontWeight: 700, color: '#0090D3', marginBottom: '8px' }}>
                      {summary.totalRecords.toLocaleString('pt-BR')} registros
                    </div>

                    <div style={{ fontSize: '11px', color: '#64748B', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <span>De: {formatDate(summary.oldestDate)}</span>
                      <span>Até: {formatDate(summary.newestDate)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'tasks' && (
        <div
          style={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            border: '1px solid #E2E8F0',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              padding: '16px 20px',
              borderBottom: '1px solid #E2E8F0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#0F172A' }}>
                Histórico de Tarefas e Execuções
              </h3>
              <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748B' }}>
                Registro de todas as operações de arquivamento, desarquivamento e purga realizadas
              </p>
            </div>
            <button
              onClick={loadTasks}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: '1px solid #CBD5E1',
                backgroundColor: '#fff',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <RefreshCw size={14} />
              <span>Atualizar</span>
            </button>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
              <thead>
                <tr style={{ backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0', color: '#475569', fontWeight: 600 }}>
                  <th style={{ padding: '12px 16px' }}>Iniciado Em</th>
                  <th style={{ padding: '12px 16px' }}>Tipo de Tarefa</th>
                  <th style={{ padding: '12px 16px' }}>Status</th>
                  <th style={{ padding: '12px 16px' }}>Registros Processados</th>
                  <th style={{ padding: '12px 16px' }}>Detalhes</th>
                  <th style={{ padding: '12px 16px' }}>Executor</th>
                </tr>
              </thead>
              <tbody>
                {loadingTasks ? (
                  <tr>
                    <td colSpan={6} style={{ padding: '40px', textAlign: 'center', color: '#64748B' }}>
                      <RefreshCw size={24} className="spin" style={{ margin: '0 auto 8px' }} />
                      <div>Carregando histórico de tarefas...</div>
                    </td>
                  </tr>
                ) : tasks.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ padding: '40px', textAlign: 'center', color: '#64748B' }}>
                      Nenhuma tarefa registrada até o momento.
                    </td>
                  </tr>
                ) : (
                  tasks.map((task) => (
                    <tr key={task.id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                      <td style={{ padding: '12px 16px', color: '#334155', whiteSpace: 'nowrap' }}>
                        {formatDate(task.startedAt)}
                      </td>
                      <td style={{ padding: '12px 16px', fontWeight: 600, color: '#0F172A' }}>
                        {task.taskType === 'ARCHIVE_30_DAYS'
                          ? 'Arquivamento (30 Dias)'
                          : task.taskType === 'UNARCHIVE_PERIOD'
                          ? 'Desarquivamento de Período'
                          : task.taskType === 'PURGE_6_MONTHS'
                          ? 'Purga de 6 Meses'
                          : task.taskType}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        {task.status === 'COMPLETED' ? (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '3px 8px',
                              borderRadius: '6px',
                              backgroundColor: '#DCFCE7',
                              color: '#166534',
                              fontSize: '12px',
                              fontWeight: 600,
                            }}
                          >
                            <CheckCircle2 size={14} />
                            Concluído
                          </span>
                        ) : task.status === 'RUNNING' ? (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '3px 8px',
                              borderRadius: '6px',
                              backgroundColor: '#EFF6FF',
                              color: '#1E40AF',
                              fontSize: '12px',
                              fontWeight: 600,
                            }}
                          >
                            <RefreshCw size={14} className="spin" />
                            Em Execução
                          </span>
                        ) : (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '3px 8px',
                              borderRadius: '6px',
                              backgroundColor: '#FEE2E2',
                              color: '#991B1B',
                              fontSize: '12px',
                              fontWeight: 600,
                            }}
                          >
                            <XCircle size={14} />
                            Falhou
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px', fontWeight: 600, color: '#0090D3' }}>
                        {task.recordsCount.toLocaleString('pt-BR')}
                      </td>
                      <td style={{ padding: '12px 16px', color: '#64748B', maxWidth: '300px' }}>
                        {task.details || '-'}
                      </td>
                      <td style={{ padding: '12px 16px', color: '#475569' }}>
                        {task.TriggeredBy ? task.TriggeredBy.name : 'Agendador Automático'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedLogForDiff && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '16px',
          }}
        >
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '680px',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            }}
          >
            <div
              style={{
                padding: '20px 24px',
                borderBottom: '1px solid #f0f0f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#1a1a1a' }}>
                  Diff de Alteração — {selectedLogForDiff.tableName} #{selectedLogForDiff.recordId}
                </h3>
                <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>
                  {formatDate(selectedLogForDiff.changedAt)} por {selectedLogForDiff.ChangedBy?.name || 'Sistema'}
                </p>
              </div>
              <button
                onClick={() => setSelectedLogForDiff(null)}
                style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#999', fontSize: '18px' }}
              >
                ✕
              </button>
            </div>

            <div style={{ padding: '24px' }}>
              {selectedLogForDiff.diff && Object.keys(selectedLogForDiff.diff).length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {Object.entries(selectedLogForDiff.diff).map(([field, values]) => (
                    <div
                      key={field}
                      style={{
                        padding: '12px 16px',
                        borderRadius: '8px',
                        border: '1px solid #E2E8F0',
                        backgroundColor: '#F8FAFC',
                      }}
                    >
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#0F172A', marginBottom: '8px' }}>
                        Campo: <span style={{ color: '#0090D3' }}>{field}</span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div
                          style={{
                            backgroundColor: '#FEE2E2',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            color: '#991B1B',
                          }}
                        >
                          <div style={{ fontWeight: 600, marginBottom: '2px' }}>De:</div>
                          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                            {JSON.stringify(values.old, null, 2) ?? 'null'}
                          </pre>
                        </div>
                        <div
                          style={{
                            backgroundColor: '#DCFCE7',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            color: '#166534',
                          }}
                        >
                          <div style={{ fontWeight: 600, marginBottom: '2px' }}>Para:</div>
                          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                            {JSON.stringify(values.new, null, 2) ?? 'null'}
                          </pre>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : selectedLogForDiff.action === 'DELETE' ? (
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#991B1B', marginBottom: '8px' }}>
                    Snapshot dos Dados Excluídos:
                  </div>
                  <pre
                    style={{
                      padding: '16px',
                      backgroundColor: '#F8FAFC',
                      borderRadius: '8px',
                      fontSize: '12px',
                      color: '#334155',
                      overflowX: 'auto',
                    }}
                  >
                    {JSON.stringify(selectedLogForDiff.oldValue, null, 2)}
                  </pre>
                </div>
              ) : (
                <div style={{ textAlign: 'center', color: '#64748B', padding: '20px' }}>
                  Nenhuma diferença de campos individualizada registrada para este evento.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <UnarchiveModal
        isOpen={isUnarchiveModalOpen}
        onClose={() => setIsUnarchiveModalOpen(false)}
        onSuccess={() => {
          setActionMessage({ type: 'success', text: 'Período desarquivado com sucesso para a auditoria ativa.' })
          loadStats()
          if (activeTab === 'live') loadLiveLogs()
          if (activeTab === 'archive') loadArchiveSummaries()
          if (activeTab === 'tasks') loadTasks()
        }}
      />
    </div>
  )
}

export default AuditDashboard
